import animationCreator from 'Engine/animations/animationCreator';
import Event from 'Engine/utils/event';
import EntryPoint from 'Engine/EntryPoint';
import { SimpleMesh } from 'pixi.js';

const sequenceAnimationsData = {};
const spinePool = {};
const defaultFPS = 30;
const maxTextureSize = 240 * 1000000;
let textureSize = 0;

export default class SequenceAnimation extends PIXI.AnimatedSprite {
  constructor(spineAnimationType, options = { skinName: '' }) {
    super([PIXI.Texture.EMPTY]);
    this._options = options;
    if (!sequenceAnimationsData[spineAnimationType]) this._createSpineAndDefaultAnimationsData(spineAnimationType, this.targetFPS);
    this._animationType = spineAnimationType;
    this._queue = [];
    this._visible = true;
    this._autoUpdate = true;
    this.onComplete = this._onComplete.bind(this);
    this.onFrameChange = this._onFrameChange.bind(this);
    this.onLoop = this._onLoop.bind(this);
    this.onCompleteAnimation = new Event();
    this.onFrameChangeAnimation = new Event();
    this.onLoopAnimation = new Event();
  }

  set visible (value) {
    if (value === this._visible) return;
    this._visible = value;
    if (this._visible) {
      if (this._cacheCleared) {
        this._setAsUsed(this._animationName);
        this.updateTexture();
      }
    } else {
      this._clearCache();
    }
    this._updateTickerState();
  }

  get visible () {
    return this._visible;
  }

  set autoUpdate(value) {
    if (value !== this._autoUpdate) {
      this._autoUpdate = value;
      this._updateTickerState();
    }
  }

  get autoUpdate() {
    return this._autoUpdate;
  }

  setAnimation(animationName, loop, clearQueue = true) {
    if (!sequenceAnimationsData[this._animationType] || !sequenceAnimationsData[this._animationType][animationName]) {
      throw new Error("Animation not found: " + animationName);
      return;
    }

    if (clearQueue) {
      this._removeUsedAllWithout(animationName);
      this._queue = [];
    }

    this.loop = !!loop;
    if (this._textures !== sequenceAnimationsData[this._animationType][animationName].textures) {
      this._previousAnimationName = this._animationName;
      this._animationName = animationName;
      this._pivots = sequenceAnimationsData[this._animationType][this._animationName].pivots;
      this._textures = sequenceAnimationsData[this._animationType][this._animationName].textures;
      this._durations = sequenceAnimationsData[this._animationType][this._animationName].durations;
      this.durationOneFrame = this._durations[0];
      this._previousFrame = null;
      this._setAsUsed(this._animationName);
      this.gotoAndStop(0);
      this.updateTexture();
      if (this._previousAnimationName && !this._inQueue(this._previousAnimationName))
        this._removeUsed(this._previousAnimationName);
    }
    this.gotoAndPlay(0);
  }

  addAnimation(animationName, loop, delay = 0) {
    if (!sequenceAnimationsData[this._animationType] || !sequenceAnimationsData[this._animationType][animationName]) {
      throw new Error("Animation not found: " + animationName);
      return;
    }
    this._queue.push([animationName, loop, delay]);
  }

  play() {
    {
      if (this._playing)
      {
        return;
      }

      this._playing = true;
      if (this._visible && this._autoUpdate && !this._isConnectedToTicker)
      {
        PIXI.Ticker.shared.add(this.update, this, PIXI.UPDATE_PRIORITY.HIGH);
        this._isConnectedToTicker = true;
      }
    }
  }

  stop() {
    if (!this._playing)
    {
      return;
    }

    this._playing = false;
    if (this._visible && this._autoUpdate && this._isConnectedToTicker)
    {
      PIXI.Ticker.shared.remove(this.update, this);
      this._isConnectedToTicker = false;
    }
  }

  updateTexture() {
    const currentFrame = this.currentFrame;
    const previousFrame = this._previousFrame
    const currentTextureIsEmpty = this._textures[currentFrame] === PIXI.Texture.EMPTY;
    if (previousFrame === currentFrame && !currentTextureIsEmpty) return;

    const animationData = sequenceAnimationsData[this._animationType] && sequenceAnimationsData[this._animationType][this._animationName];
    if (animationData && currentTextureIsEmpty) {
      const textureData = this._createTexture(this._animationType, this._animationName, this.durationOneFrame * currentFrame);
      animationData.textures[currentFrame] = textureData.texture;
      animationData.pivots[currentFrame] = textureData.pivot;
      textureSize = EntryPoint.Game.app.renderer.texture.managedTextures.reduce((prev, {width, height}) => prev + width * height, 0) * 4;
      // console.log(`${(textureSize / 1000000).toFixed(2)}mb`)
    }

    this._previousFrame = currentFrame;

    this._texture = this._textures[currentFrame];
    this._textureID = -1;
    this._textureTrimmedID = -1;
    this._cachedTint = 0xFFFFFF;
    this.uvs = this._texture._uvs.uvsFloat32;

    if (this.updateAnchor)
    {
      this._anchor.copyFrom(this._texture.defaultAnchor);
    }

    if (this.onFrameChange)
    {
      this.onFrameChange(this.currentFrame);
    }

    if (animationData && (animationData.used.length <= 1 || textureSize >= maxTextureSize)) {
      this._destroyFirstUnusedTexture(animationData);
    }
  }

  setSkinByName(skinName = '') {
    if (!skinName || this._options.skinName === skinName) return;
    this._options.skinName = skinName;
    Object.keys(spinePool[this._animationType]).forEach(animationName => {
      const spine = spinePool[this._animationType][animationName];
      this._destroyAllTexturesByAnimationName(animationName);
      spine.skeleton.setSkinByName(skinName);
    });
  }

  _onFrameChange(currentFrame) {
    if (this._pivots) this.pivot.set(this._pivots[currentFrame].x, this._pivots[currentFrame].y);
    this.onFrameChangeAnimation.call(currentFrame);
  }

  _onLoop() {
    this.onLoopAnimation.call();
  }

  _onComplete(data) {
    this._tryClearAnimationCacheWithoutUsedTexture(this._animationName);
    if (this._queue[0]) {
      const [animationName, loop, delay] = this._queue.shift();
      setTimeout(() => this.setAnimation(animationName, loop, false), delay)
    }
    this.onCompleteAnimation.call(data);
  }

  _createSpineAndDefaultAnimationsData(spineAnimationType, fps) {
    spinePool[spineAnimationType] = {};
    sequenceAnimationsData[spineAnimationType] = {};
    animationCreator.getSpineData(spineAnimationType).animations.forEach(animationData => {
      spinePool[spineAnimationType][animationData.name] = animationCreator.createAnimation(spineAnimationType);
      spinePool[spineAnimationType][animationData.name].autoUpdate = false;
      if (this._options.skinName) spinePool[spineAnimationType][animationData.name].skeleton.setSkinByName(this._options.skinName);
      this._createDefaultAnimationData(spineAnimationType, animationData, fps);
    });
  }

  _createDefaultAnimationData(spineAnimationType, spineAnimationData, fps) {
    const durationOneFrame = 1 / (fps - 1) * 1000;
    const defaultPivot = { x: 0, y: 0 };

    const framesCount = Math.floor(spineAnimationData.duration * fps);
    sequenceAnimationsData[spineAnimationType][spineAnimationData.name] = {
      textures: new Array(framesCount).fill(PIXI.Texture.EMPTY),
      durations: new Array(framesCount).fill(durationOneFrame),
      pivots: new Array(framesCount).fill(defaultPivot),
      used: []
    };
  }

  _createTexture(spineAnimationType, animationName, time) {
    const spine = spinePool[spineAnimationType][animationName];
    if (!spine.state.tracks[0] || spine.state.tracks[0].animation.name !== animationName) {
      spine.state.setAnimation(0, animationName, false);
    }
    const track = spine.state.tracks[0];
    track.trackTime = time / 1000;
    spine.skeleton.setToSetupPose();
    spine.update(0);
    this._updateMeshes(spine);
    const localBounds = spine.getLocalBounds(null, true);
    const originalBounds = {...localBounds};
    const bounds = new PIXI.Rectangle().copyFrom(localBounds);
    const animationData = sequenceAnimationsData[spineAnimationType][animationName]
    const width = bounds.width;
    const height = bounds.height;
    bounds.width = this._nextDivisibleBy4(width);
    bounds.height = this._nextDivisibleBy4(height);
    bounds.x -= (bounds.width - width) / 2;
    bounds.y -= (bounds.height - height) / 2;
    bounds.x = Math.floor(bounds.x);
    bounds.y = Math.floor(bounds.y);
    const texture = EntryPoint.Game.app.renderer.generateTexture(spine, {region: bounds, multisample: PIXI.MSAA_QUALITY.NONE})

    const textureData = {texture, pivot: {x: -bounds.x, y: -bounds.y}};
    return textureData;
  }

  _updateMeshes(displayObject) {
    displayObject.children.forEach((child) => {
      if (child instanceof SimpleMesh) {
        child.autoUpdate = false;
        child.geometry.getBuffer('aVertexPosition').update();
      } else {
        this._updateMeshes(child);
      }
    });
  }

  _tryClearAnimationCache(animationName) {
    if (this._checkUsedGlobal(animationName)) return;
    this._destroyAllTexturesByAnimationName(animationName);
  }

  _destroyAllTexturesByAnimationName(animationName) {
    const animationData = sequenceAnimationsData[this._animationType][animationName];
    if (!animationData) return;
    animationData.textures.forEach((texture, index) => {
      this._destroyTextureByIndex(index, animationData);
    });
  }

  _tryClearAnimationCacheWithoutUsedTexture(animationName) {
    const animationData = sequenceAnimationsData[this._animationType][animationName];
    if (!animationData || animationData.used.some(animation => animation._playing)) return;
      animationData.textures.forEach((texture, index) => {
      if (animationData.used.every(animation => animation._texture !== texture)){
          this._destroyTextureByIndex(index, animationData);
      }
    });
  }

  _destroyFirstUnusedTexture(animationData) {
    const textureIndex = animationData.textures.findIndex((texture) => texture !== PIXI.Texture.EMPTY && animationData.used.every(animation => animation._texture !== texture));
    if (textureIndex !== -1) {
      this._destroyTextureByIndex(textureIndex, animationData);
    }
  }

  _destroyTextureByIndex(index, animationData) {
    if (animationData.textures[index] === PIXI.Texture.EMPTY) return;
    // don't destroy the texture, will destroy _texture from other sprites
    textureSize -= animationData.textures[index].baseTexture.width * animationData.textures[index].baseTexture.height * 4
    animationData.textures[index].baseTexture.destroy();
    animationData.textures[index] = PIXI.Texture.EMPTY
    animationData.pivots[index] = { x: 0, y: 0 };
  }

  _clearCache() {
      this._removeUsedAllWithout();
      this._cacheCleared = true;
  }

  _setAsUsed(animationName) {
    const animationData = sequenceAnimationsData[this._animationType][animationName];
    if (animationData && !animationData.used.includes(this)) animationData.used.push(this);
  }

  _removeUsed(animationName) {
    const animationData = sequenceAnimationsData[this._animationType][animationName];
    animationData.used = animationData.used.filter( obj => obj !== this);
    this._tryClearAnimationCache(animationName);
  }

  _removeUsedAllWithout(animationName) {
    const animations = Object.keys(sequenceAnimationsData[this._animationType])
    animations.forEach(animation => {
      if (animation !== animationName) {
        this._removeUsed(animation);
      }
    })
  }

  _checkUsedGlobal(animationName) {
    const animationData = sequenceAnimationsData[this._animationType][animationName]
    return animationData.used.length > 0;
  }

  _updateTickerState() {
    if ((!this._visible || !this._autoUpdate) && this._isConnectedToTicker)
    {
      PIXI.Ticker.shared.remove(this.update, this);
      this._isConnectedToTicker = false;
    }
    else if (this._visible && this._autoUpdate && !this._isConnectedToTicker && this._playing)
    {
      PIXI.Ticker.shared.add(this.update, this);
      this._isConnectedToTicker = true;
    }
  }

  _nextPow2(v) {
    v += v === 0 ? 1 : 0;
    --v;
    v |= v >>> 1;
    v |= v >>> 2;
    v |= v >>> 4;
    v |= v >>> 8;
    v |= v >>> 16;
    return v + 1;
  }

  _nextDivisibleBy4(v) {
    return v + (4 - (v % 4 || 4));
  }


  _inQueue(animation) {
    return this._queue.some(([animationName]) => animationName === animation)
  }
}

SequenceAnimation.prototype.targetFPS = defaultFPS;
